import { isLocaleSwitcherEnabled, isRuntimeLocalesEnabled, } from "./locales/runtimeLocaleFlags";
const NAMESPACE_PRELOAD_TIMEOUT_MS = 2000;
/*
 * Both runtime-locale flags off — the state of every user until one is set — so
 * the boot path stays what it was before AT2-19905: one dynamic import, no
 * manifest request, and no waterfall of chunk fetches before bootstrap can even
 * be requested. That matters more here than in arbook: an LTI launch is a cold
 * load inside an LMS iframe. The flag readers come from a leaf module
 * (runtimeLocaleFlags → stickyFlag) precisely so this decision costs the entry
 * chunk nothing.
 */
if (!isLocaleSwitcherEnabled() && !isRuntimeLocalesEnabled()) {
    import("./bootstrap");
}
else {
    /*
     * The published-locale set has to be in place before the bootstrap chunk is
     * evaluated: bootstrap statically imports I18nProvider → i18n.ts, which
     * resolves getAppLocale() at module eval. A failed or slow manifest fetch
     * resolves anyway (see locales/publishedLocales.ts) — boot is never blocked.
     *
     * i18n.ts is imported here rather than left to bootstrap so the current
     * locale's namespaces can be awaited before the first paint. For a locale
     * outside ./locales/{en,uk} they load from the CDN, and painting without them
     * shows English from FALLBACK_CHAIN and then re-renders. Importing it earlier
     * keeps the init-order contract with the lesson_builder remote (i18next
     * initialised before any remote loads) — bootstrap's static import reuses this
     * same module.
     */
    void (async () => {
        const { loadPublishedLocales } = await import("./locales/publishedLocales");
        await loadPublishedLocales();
        // Dynamic import, like i18n below: a static getAppLocale import would drag
        // publishedLocales → the SDK's parseManifest into the entry chunk and undo
        // the flags-off fast path above. Inside this branch the module graph is
        // already loading, so it costs nothing.
        const { getAppLocale, getBundledLocales } = await import("./locales/getAppLocale");
        const { default: i18n, APP_NAMESPACES } = await import("./locales/i18n");
        // Only a CDN-only locale needs its namespaces awaited before first paint —
        // bundled ones (en, uk) resolve from memory, so the guard just skips a
        // no-op await (shape-mirror of arbook's runtime ≠ build guard; its
        // fallback-chain leak never applied here since both FALLBACK_CHAIN locales
        // are bundled). After a switch the set is warm from the switch-time
        // prefetch, so the race resolves from IndexedDB. The timeout only ever
        // fires behind a slow or dead endpoint — which then paints English rather
        // than hanging.
        if (!getBundledLocales().has(getAppLocale())) {
            await Promise.race([
                i18n.loadNamespaces(APP_NAMESPACES),
                new Promise((resolve) => setTimeout(resolve, NAMESPACE_PRELOAD_TIMEOUT_MS)),
            ]);
        }
        await import("./bootstrap");
    })();
}
